import { useRef, useState, useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { PerspectiveCamera } from '@react-three/drei';
import { Group } from 'three';
import { useAppStore } from '../../store/useAppStore';
import { useResponsiveValues } from '../../hooks/viewport/useResponsiveValues';
import { useSpiralAnimation } from '../../hooks/animation/useSpiralAnimation';
import { useMouseTracking } from '../../hooks/interaction/useMouseTracking';
import { useSpiralMaterials } from '../../hooks/materials/useSpiralMaterials';
import { useMaterialsAnimation } from '../../hooks/animation/useMaterialsAnimation';
import { usePositionAnimation } from '../../hooks/animation/usePositionAnimation';
import { GalleryItem } from './GalleryItem';
import type { Project } from '../../types';

interface SpinGalleryProps {
  projects: Project[];
  allMediaLoaded: boolean;
}

export const SpinGallery: React.FC<SpinGalleryProps> = ({
  projects,
  allMediaLoaded,
}) => {
  const { selectedIndex, setSelected } = useAppStore(state => ({
    selectedIndex: state.selectedIndex,
    setSelected: state.setSelected,
  }));
  const groupRef = useRef<Group>(null);
  const hoveredRef = useRef<number | null>(null);
  const [transitionProgress, setTransitionProgress] = useState(0);
  const { viewport } = useThree();
  const values = useResponsiveValues(viewport);

  const {
    scrollRef,
    initAnimation,
    setInitAnimation,
    isInitialAnimationComplete,
    velocity,
  } = useSpiralAnimation(groupRef, values);

  const mousePos = useMouseTracking(selectedIndex ?? -1);
  const materials = useSpiralMaterials(projects, values);

  useMaterialsAnimation({
    materials,
    hoveredRef,
    selectedIndex,
    mousePos,
    isInitialAnimationComplete,
    initAnimation,
    scrollRef,
    projectsPerLoop: values.projectsPerLoop,
    velocity,
  });

  usePositionAnimation({
    groupRef,
    allImagesLoaded: allMediaLoaded,
    isInitialAnimationComplete,
    setInitAnimation,
    initAnimation,
    scrollRef,
    selectedIndex,
    projects,
    values,
    setTransitionProgress,
    onNearestProjectChange: index => {
      useAppStore.setState({ nearestProjectIndex: index });
    },
  });

  useEffect(() => {
    if (transitionProgress >= 0.80) {
      useAppStore.getState().setTransitionComplete(true);
    } else {
      useAppStore.getState().setTransitionComplete(false);
    }
  }, [transitionProgress]);

  return (
    <>
      <PerspectiveCamera makeDefault position={[0, 0, 15]} fov={60} />
      <group ref={groupRef}>
        {projects.map((project, index) => (
          <GalleryItem
            key={project.id}
            project={project}
            index={index}
            originalIndex={index}
            totalProjects={projects.length}
            material={materials[index]}
            imageScale={values.imageScale}
            isSelected={selectedIndex === index}
            onSelect={() => {
              setSelected(
                selectedIndex === index ? null : project,
                selectedIndex === index ? null : index
              );
            }}
            onHover={isHovered => {
              hoveredRef.current = isHovered ? index : null;
            }}
            transitionProgress={transitionProgress}
          />
        ))}
      </group>
    </>
  );
};
